/**
 * Activity Timeline API Service
 * Endpoint: GET /v1/activity-timeline/resident/{residentId}
 *
 * The backend returns a heterogeneous array of raw domain objects
 * (vitals, medications, MAR entries, notes, etc.) without a unified
 * {type, title, description, timestamp} shape.
 *
 * This service detects the activity type from field presence and
 * constructs a normalized ActivityTimelineEvent for every item.
 */

import apiClient from './apiClient';
import { parseApiDateTime } from '../utils/dateUtils';

// ─── Normalized shape consumed by the UI ─────────────────────────────────────

export type ActivityEventType =
  | 'NOTE'
  | 'VITAL'
  | 'MAR'
  | 'APPOINTMENT'
  | 'DOCUMENT'
  | 'FACESHEET'
  | 'PRESCRIBED_MEDICATION'
  | 'CARE_PLAN'
  | 'PHARMACY_ORDER';

export interface ActivityTimelineEvent {
  /** Unique identifier (from the raw object) */
  id: string;
  /** Detected activity category */
  type: ActivityEventType;
  /** Human-readable title */
  title: string;
  /** Human-readable summary */
  description: string;
  /** ISO-8601 timestamp string, already resolved from the best available field */
  timestamp: string;
  /** Display name of the person who created / last updated the record */
  createdBy: string;
  /** Original raw object for any further use */
  raw: Record<string, any>;
  /** Optional heading provided by the backend */
  heading?: string;
  /** Optional custom color for the event */
  color?: string;
}

// ─── Field-based type detection ───────────────────────────────────────────────

/**
 * Detect activity type from the raw object's fields.
 * Priority order matches the sidebar colour scheme (same as the local aggregation).
 */
function detectType(r: Record<string, any>): ActivityEventType {
  // If the backend already sends an explicit type field, use it first.
  const explicit = (r.entityName || r.activityType || r.type || r.eventType || '') as string;
  if (explicit) {
    const up = explicit.toUpperCase();
    if (up.includes('VITAL')) return 'VITAL';
    if (up.includes('PRESCRIBED') || up.includes('PRESCRIPTION')) return 'PRESCRIBED_MEDICATION';
    if (up.includes('MAR') || up.includes('NURSE')) return 'MAR';
    if (up.includes('APPOINTMENT')) return 'APPOINTMENT';
    if (up.includes('DOCUMENT') || up.includes('DOC')) return 'DOCUMENT';
    if (up.includes('CARE') || up.includes('CAREPLAN')) return 'CARE_PLAN';
    if (up.includes('PHARMACY') || up.includes('ORDER')) return 'PHARMACY_ORDER';
    if (up.includes('NOTE')) return 'NOTE';
    if (up.includes('FACE') || up.includes('SHEET')) return 'FACESHEET';
  }

  // ── field-presence heuristics ────────────────────────────────────────────
  // Vitals: EMR-vitals fields
  if (r.dailyBloodPressure !== undefined || r.vitalDate !== undefined || r.pulseRate !== undefined)
    return 'VITAL';

  // Prescribed medication (singular med / dose / schedule)
  if ((r.med !== undefined || r.medicationName !== undefined) && (r.dose !== undefined || r.dosage !== undefined) && r.schedule !== undefined)
    return 'PRESCRIBED_MEDICATION';

  // MAR / nurse entry (medication + initials / reason)
  if ((r.medication !== undefined || r.medicationName !== undefined) && (r.initials !== undefined || r.reason !== undefined))
    return 'MAR';

  // Pharmacy / medication orders (has medicationItems array)
  if (Array.isArray(r.medicationItems) || r.prescriptionNumber !== undefined)
    return 'PHARMACY_ORDER';

  // Appointment
  if (r.appointmentDate !== undefined || r.doctorName !== undefined)
    return 'APPOINTMENT';

  // Document
  if (r.fileName !== undefined || r.documentName !== undefined)
    return 'DOCUMENT';

  // Care plan
  if (r.diagnosticsPlan !== undefined || r.dailyActivities !== undefined || r.goals !== undefined)
    return 'CARE_PLAN';

  // Clinical note
  if (r.content !== undefined && r.noteType !== undefined)
    return 'NOTE';

  // Resident facesheet / general update
  if (r.firstName !== undefined && r.lastName !== undefined)
    return 'FACESHEET';

  return 'NOTE'; // safe default
}

// ─── Per-type title & description builders ────────────────────────────────────

function buildTitle(type: ActivityEventType, r: Record<string, any>): string {
  switch (type) {
    case 'VITAL':
      return 'Vital Signs Recorded';

    case 'PRESCRIBED_MEDICATION': {
      const name = r.med || r.medicationName || r.name || 'Medication';
      return `Prescribed: ${name}`;
    }

    case 'MAR': {
      const status = r.status || r.initials || 'Entry';
      const name = r.medication || r.medicationName || r.med || '';
      return `Medication ${status}${name ? ` – ${name}` : ''}`;
    }

    case 'PHARMACY_ORDER': {
      const num = r.prescriptionNumber || r.orderNumber || '';
      return `Pharmacy Order${num ? `: ${num}` : ''}`;
    }

    case 'APPOINTMENT': {
      const kind = r.appointmentType || r.type || 'Appointment';
      return `Appointment: ${kind}`;
    }

    case 'DOCUMENT':
      return `Document: ${r.documentName || r.fileName || 'Uploaded'}`;

    case 'CARE_PLAN':
      return 'Care Plan Updated';

    case 'NOTE': {
      const kind = r.noteType || 'General';
      return `${kind} Note`;
    }

    case 'FACESHEET':
      return 'Facesheet Updated';

    default:
      return 'Activity Recorded';
  }
}

function buildDescription(type: ActivityEventType, r: Record<string, any>): string {
  if (r.changeDescription) return String(r.changeDescription);

  const na = (v: any) => (v !== undefined && v !== null && v !== '' ? String(v) : null);

  switch (type) {
    case 'VITAL': {
      const parts: string[] = [];
      if (na(r.dailyBloodPressure)) parts.push(`BP: ${r.dailyBloodPressure}`);
      if (na(r.dailyWeight)) parts.push(`Weight: ${r.dailyWeight}`);
      if (na(r.temperature)) parts.push(`Temp: ${r.temperature}`);
      if (na(r.pulseRate)) parts.push(`Pulse: ${r.pulseRate}`);
      if (na(r.respirationRate)) parts.push(`Resp: ${r.respirationRate}`);
      if (na(r.spO2)) parts.push(`SpO₂: ${r.spO2}`);
      if (na(r.painLevel)) parts.push(`Pain: ${r.painLevel}`);
      return parts.length ? parts.join(', ') : 'Vitals recorded.';
    }

    case 'PRESCRIBED_MEDICATION': {
      const parts: string[] = [];
      if (na(r.dose || r.dosage)) parts.push(`Dose: ${r.dose ?? r.dosage}`);
      if (na(r.route)) parts.push(`Route: ${r.route}`);
      if (na(r.schedule)) parts.push(`Schedule: ${r.schedule}`);
      return parts.length ? parts.join(', ') : 'Medication prescribed.';
    }

    case 'MAR': {
      const med = r.medication || r.medicationName || r.med || '';
      const dosage = r.dosage || r.dose || '';
      const reason = r.reason || r.status || '';
      const parts = [med, dosage ? `${dosage}` : '', reason ? `(${reason})` : ''].filter(Boolean);
      return parts.join(' ') || 'MAR entry recorded.';
    }

    case 'PHARMACY_ORDER': {
      const meds = Array.isArray(r.medicationItems)
        ? r.medicationItems.map((i: any) => i.medicationName || i.name || '').filter(Boolean).join(', ')
        : '';
      return meds ? `Medications: ${meds}` : 'Pharmacy order recorded.';
    }

    case 'APPOINTMENT': {
      const doctor = r.doctorName || r.doctor || 'Physician';
      const reason = r.reason || '';
      return reason
        ? `With ${doctor} for ${reason}`
        : `With ${doctor}`;
    }

    case 'DOCUMENT':
      return `Type: ${r.fileType || r.mimeType || 'General'}`;

    case 'CARE_PLAN':
      return r.goals || 'Resident care plan goals and instructions were reviewed or updated.';

    case 'NOTE':
      return r.content || r.note || 'Clinical note recorded.';

    case 'FACESHEET':
      return 'Resident personal information, contacts, or clinical identifiers were updated.';

    default:
      return 'Activity recorded.';
  }
}

function toIsoTimestamp(value: unknown): string | null {
  const parsed = parseApiDateTime(value as string | number | Date | number[] | null);
  return parsed ? parsed.toISOString() : null;
}

function resolveTimestamp(type: ActivityEventType, r: Record<string, any>): string {
  // actionAt is the canonical audit timestamp from the activity-timeline API
  const actionAt = toIsoTimestamp(r.actionAt);
  if (actionAt) return actionAt;

  let fallback: unknown;
  switch (type) {
    case 'VITAL':
      fallback = r.vitalDate || r.createdAt || r.createdOn;
      break;
    case 'PRESCRIBED_MEDICATION':
      fallback = r.updatedOn || r.createdOn || r.updatedAt || r.createdAt;
      break;
    case 'MAR':
      fallback = r.noteDate || r.createdOn || r.createdAt;
      break;
    case 'APPOINTMENT':
      fallback = r.appointmentDate
        ? `${r.appointmentDate}T${r.appointmentTime || '00:00:00'}`
        : r.createdAt;
      break;
    default:
      fallback = r.updatedAt || r.createdAt || r.updatedOn || r.createdOn;
  }

  return toIsoTimestamp(fallback) ?? new Date().toISOString();
}

function resolveAuthor(r: Record<string, any>): string {
  return (
    r.actionBy ||
    r.createdByName ||
    r.updatedByName ||
    r.authorName ||
    r.createdBy ||
    r.updatedBy ||
    r.authorId ||
    'System'
  );
}

// ─── Main mapper ──────────────────────────────────────────────────────────────

function mapRawToEvent(raw: Record<string, any>, index: number): ActivityTimelineEvent {
  const type = detectType(raw);
  const id = raw.id || raw.apiId || `timeline-${index}-${Date.now()}`;
  const title = buildTitle(type, raw);
  const description = buildDescription(type, raw);
  const timestamp = resolveTimestamp(type, raw);
  const createdBy = resolveAuthor(raw);
  const heading = raw.heading || raw.title || raw.subject;
  const color = raw.color;

  return { id, type, title, description, timestamp, createdBy, raw, heading, color };
}

// ─── Service ──────────────────────────────────────────────────────────────────

const BASE_URL = '/v1/activity-timeline';

class ActivityTimelineService {
  /**
   * Fetch and normalize activity events for a resident.
   * Handles plain array, Spring Page { content: [] }, and single-object responses.
   */
  async getResidentTimeline(residentId: string): Promise<ActivityTimelineEvent[]> {
    const response = await apiClient.get<any>(
      `${BASE_URL}/resident/${residentId}`
    );

    const data = response.data;

    let rawItems: Record<string, any>[] = [];

    if (Array.isArray(data)) {
      rawItems = data;
    } else if (data && Array.isArray(data.content)) {
      rawItems = data.content;
    } else if (data && typeof data === 'object') {
      // Single object returned – wrap it
      rawItems = [data];
    }

    return rawItems
      .filter(Boolean)
      .map((item, i) => mapRawToEvent(item as Record<string, any>, i));
  }
}

const activityTimelineService = new ActivityTimelineService();
export default activityTimelineService;
